/**
 * Playable presence characters for the Friends picker + backyard player.
 *
 * ONE authoritative registry for identity, static visual, relative size,
 * feet origin, and carry anchors. Friends UI and gameplay both derive
 * proportions from this file.
 *
 * Current visuals are static whole-character PNGs (`visualType: 'static'`).
 * Later articulated rigs can swap the visual layer without rewriting the
 * player controller, picker, or interaction systems.
 */

export type PlayableCharacterId =
  | 'big-sister'
  | 'little-brother'
  | 'baby-girl'
  | 'boy-cousin'
  | 'girl-cousin'
  | 'baby-cousin'
  | 'mom'
  | 'dad'
  | 'aunt'
  | 'aunt-2'
  | 'aunt-3'
  | 'aunt-4'
  | 'uncle'
  | 'uncle-2'
  | 'grandma'
  | 'grandpa';

/** How the character is drawn today — expand later with `'rigged'`. */
export type CharacterVisualType = 'static';

export interface CarryAnchor {
  /** World-width offset from feet toward facing. */
  x: number;
  /** World-y offset from feet (negative = up the screen). */
  y: number;
}

/**
 * Feet / ground contact — world position means where feet touch the ground.
 * Sprite CSS uses transform-origin matching these fractions of the image.
 */
export interface FeetOrigin {
  /** Horizontal origin in the sprite (0–1). */
  originX: number;
  /** Vertical origin in the sprite (0–1). Feet = 1. */
  originY: number;
}

export interface InteractionBounds {
  /** Hit radius as a fraction of character display height. */
  radiusFrac: number;
}

export interface PlayableCharacterDef {
  id: PlayableCharacterId;
  displayName: string;
  /** Public art path for the current static visual (PNG). Runtime may rewrite to WebP. */
  playerSrc: string;
  /** Runtime PNG pixel size after cleanup / tight crop. */
  imageWidth: number;
  imageHeight: number;
  /**
   * Normalized height relative to adult male = 1.0.
   * Shared by Friends presentation and gameplay sizing.
   */
  relativeHeight: number;
  /**
   * Authored height in feet — drives presence sizing with yard calibration.
   * Derived from relativeHeight × REFERENCE_ADULT_HEIGHT_FEET (readability-tuned).
   */
  heightFeet: number;
  /**
   * Storybook display multiplier on top of physical feet → world-frac sizing.
   * Shared baseline so relativeHeight alone drives age proportions.
   */
  displayScale: number;
  /** Feet / ground contact for world placement. */
  feet: FeetOrigin;
  pineconeCarryAnchor: CarryAnchor;
  bucketCarryAnchor: CarryAnchor;
  interactionBounds: InteractionBounds;
  /**
   * Current visual pipeline. Controllers should branch on this later when
   * articulated rigs exist — do not assume CHARACTER === one PNG forever.
   */
  visualType: CharacterVisualType;
  /** True when a dedicated cleaned *-player cutout is the runtime asset. */
  hasPlayerCutout: boolean;
}

const BASE = `${import.meta.env.BASE_URL}art/characters`;
/** Bump when regenerating *-player.png so browsers don't keep holey cached cutouts. */
const PLAYER_CACHE_TAG = 'holefix2';
const playerUrl = (id: string) =>
  `${BASE}/${id}/${id}-player.png?v=${PLAYER_CACHE_TAG}`;

/** Adult-male reference height (feet) for relativeHeight → heightFeet. */
export const REFERENCE_ADULT_HEIGHT_FEET = 5.2;

/**
 * Shared storybook multiplier — relative age comes from `relativeHeight`,
 * not per-character displayScale hacks.
 */
export const SHARED_CHARACTER_DISPLAY_SCALE = 1.66;

/** Friends picker: tallest character art height inside the uniform card stage. */
export const FRIENDS_STAGE_HEIGHT_PX = 320;

/** Friends picker: uniform card stage fits the tallest character (relativeHeight 1.0). */
export const FRIENDS_CARD_STAGE_HEIGHT_PX = FRIENDS_STAGE_HEIGHT_PX;

const feetDefault: FeetOrigin = { originX: 0.5, originY: 1 };

function def(
  partial: Omit<PlayableCharacterDef, 'heightFeet' | 'displayScale' | 'feet' | 'visualType' | 'interactionBounds' | 'hasPlayerCutout'> &
    Partial<
      Pick<
        PlayableCharacterDef,
        | 'heightFeet'
        | 'displayScale'
        | 'feet'
        | 'visualType'
        | 'interactionBounds'
        | 'hasPlayerCutout'
      >
    >,
): PlayableCharacterDef {
  return {
    feet: feetDefault,
    visualType: 'static',
    hasPlayerCutout: true,
    displayScale: SHARED_CHARACTER_DISPLAY_SCALE,
    heightFeet: REFERENCE_ADULT_HEIGHT_FEET * partial.relativeHeight,
    interactionBounds: { radiusFrac: 0.28 },
    ...partial,
  };
}

/**
 * Central registry — adding a character later = assets + one entry here.
 * Order is the Friends picker order (kids first, then family).
 *
 * relativeHeight hierarchy (children's-game readability, not anatomy):
 *   Uncle (tallest adult) = 1.00 → Dad / Grandpa / Uncle 2 ≈ 0.94 →
 *   adult women ≈ 0.88 → Big Sister ≈ 0.74 → Boy Cousin ≈ 0.70 →
 *   Girl Cousin / Little Brother ≈ 0.62–0.64 → babies ≈ 0.48–0.50
 */
export const PLAYABLE_CHARACTERS: readonly PlayableCharacterDef[] = [
  def({
    id: 'big-sister',
    displayName: 'Big Sister',
    playerSrc: playerUrl('big-sister'),
    imageWidth: 640,
    imageHeight: 1365,
    relativeHeight: 0.74,
    pineconeCarryAnchor: { x: 0.015, y: -0.042 },
    bucketCarryAnchor: { x: 0.024, y: -0.028 },
  }),
  def({
    id: 'boy-cousin',
    displayName: 'Boy Cousin',
    playerSrc: playerUrl('boy-cousin'),
    imageWidth: 646,
    imageHeight: 1414,
    relativeHeight: 0.7,
    pineconeCarryAnchor: { x: 0.014, y: -0.044 },
    bucketCarryAnchor: { x: 0.022, y: -0.03 },
  }),
  def({
    id: 'girl-cousin',
    displayName: 'Girl Cousin',
    playerSrc: playerUrl('girl-cousin'),
    imageWidth: 724,
    imageHeight: 1449,
    relativeHeight: 0.64,
    pineconeCarryAnchor: { x: 0.015, y: -0.04 },
    bucketCarryAnchor: { x: 0.023, y: -0.027 },
  }),
  def({
    id: 'little-brother',
    displayName: 'Little Brother',
    playerSrc: playerUrl('little-brother'),
    imageWidth: 624,
    imageHeight: 1348,
    relativeHeight: 0.62,
    pineconeCarryAnchor: { x: 0.013, y: -0.036 },
    bucketCarryAnchor: { x: 0.02, y: -0.024 },
  }),
  def({
    id: 'baby-girl',
    displayName: 'Baby Girl',
    playerSrc: playerUrl('baby-girl'),
    imageWidth: 690,
    imageHeight: 1351,
    relativeHeight: 0.5,
    pineconeCarryAnchor: { x: 0.012, y: -0.03 },
    bucketCarryAnchor: { x: 0.018, y: -0.02 },
  }),
  def({
    id: 'baby-cousin',
    displayName: 'Baby Cousin',
    playerSrc: playerUrl('baby-cousin'),
    imageWidth: 543,
    imageHeight: 1076,
    relativeHeight: 0.48,
    pineconeCarryAnchor: { x: 0.012, y: -0.029 },
    bucketCarryAnchor: { x: 0.018, y: -0.019 },
  }),
  def({
    id: 'mom',
    displayName: 'Mom',
    playerSrc: playerUrl('mom'),
    imageWidth: 575,
    imageHeight: 1516,
    relativeHeight: 0.88,
    pineconeCarryAnchor: { x: 0.016, y: -0.05 },
    bucketCarryAnchor: { x: 0.026, y: -0.034 },
  }),
  def({
    id: 'dad',
    displayName: 'Dad',
    playerSrc: playerUrl('dad'),
    imageWidth: 507,
    imageHeight: 1461,
    relativeHeight: 0.94,
    pineconeCarryAnchor: { x: 0.017, y: -0.052 },
    bucketCarryAnchor: { x: 0.027, y: -0.035 },
  }),
  def({
    id: 'aunt',
    displayName: 'Aunt',
    playerSrc: playerUrl('aunt'),
    imageWidth: 528,
    imageHeight: 1443,
    relativeHeight: 0.88,
    pineconeCarryAnchor: { x: 0.016, y: -0.05 },
    bucketCarryAnchor: { x: 0.026, y: -0.034 },
  }),
  def({
    id: 'aunt-2',
    displayName: 'Aunt 2',
    playerSrc: playerUrl('aunt-2'),
    imageWidth: 530,
    imageHeight: 1520,
    relativeHeight: 0.88,
    pineconeCarryAnchor: { x: 0.016, y: -0.05 },
    bucketCarryAnchor: { x: 0.026, y: -0.034 },
  }),
  def({
    id: 'aunt-3',
    displayName: 'Aunt 3',
    playerSrc: playerUrl('aunt-3'),
    imageWidth: 574,
    imageHeight: 1384,
    relativeHeight: 0.88,
    pineconeCarryAnchor: { x: 0.016, y: -0.05 },
    bucketCarryAnchor: { x: 0.026, y: -0.034 },
  }),
  def({
    id: 'aunt-4',
    displayName: 'Aunt 4',
    playerSrc: playerUrl('aunt-4'),
    imageWidth: 478,
    imageHeight: 1381,
    relativeHeight: 0.88,
    pineconeCarryAnchor: { x: 0.016, y: -0.05 },
    bucketCarryAnchor: { x: 0.026, y: -0.034 },
  }),
  def({
    id: 'uncle',
    displayName: 'Uncle',
    playerSrc: playerUrl('uncle'),
    imageWidth: 489,
    imageHeight: 1245,
    relativeHeight: 1.0,
    pineconeCarryAnchor: { x: 0.017, y: -0.052 },
    bucketCarryAnchor: { x: 0.027, y: -0.035 },
  }),
  def({
    id: 'uncle-2',
    displayName: 'Uncle 2',
    playerSrc: playerUrl('uncle-2'),
    imageWidth: 565,
    imageHeight: 1504,
    relativeHeight: 0.94,
    pineconeCarryAnchor: { x: 0.017, y: -0.052 },
    bucketCarryAnchor: { x: 0.027, y: -0.035 },
  }),
  def({
    id: 'grandma',
    displayName: 'Grandma',
    playerSrc: playerUrl('grandma'),
    imageWidth: 474,
    imageHeight: 1437,
    relativeHeight: 0.88,
    pineconeCarryAnchor: { x: 0.016, y: -0.05 },
    bucketCarryAnchor: { x: 0.025, y: -0.033 },
  }),
  def({
    id: 'grandpa',
    displayName: 'Grandpa',
    playerSrc: playerUrl('grandpa'),
    imageWidth: 518,
    imageHeight: 1388,
    relativeHeight: 0.94,
    pineconeCarryAnchor: { x: 0.017, y: -0.052 },
    bucketCarryAnchor: { x: 0.027, y: -0.035 },
  }),
] as const;

export const DEFAULT_PLAYABLE_CHARACTER_ID: PlayableCharacterId = 'big-sister';

/**
 * Character that appears when first entering each backyard.
 * Friends picks in a yard are remembered separately per yard.
 */
export const DEFAULT_CHARACTER_BY_BACKYARD: Readonly<
  Record<'hubert' | 'raleigh' | 'spanish-fork', PlayableCharacterId>
> = {
  hubert: 'big-sister',
  raleigh: 'boy-cousin',
  'spanish-fork': 'grandma',
};

const BY_ID = new Map(
  PLAYABLE_CHARACTERS.map((c) => [c.id, c] as const),
);

export function isPlayableCharacterId(value: string): value is PlayableCharacterId {
  return BY_ID.has(value as PlayableCharacterId);
}

export function getPlayableCharacter(
  id: PlayableCharacterId,
): PlayableCharacterDef {
  return BY_ID.get(id) ?? BY_ID.get(DEFAULT_PLAYABLE_CHARACTER_ID)!;
}

export function listPlayableCharacters(): readonly PlayableCharacterDef[] {
  return PLAYABLE_CHARACTERS;
}

/** Friends card stage height in px from shared relativeHeight. */
export function friendsStageHeightPx(
  character: PlayableCharacterDef,
  stageHeightPx: number = FRIENDS_STAGE_HEIGHT_PX,
): number {
  return Math.round(stageHeightPx * character.relativeHeight);
}
